import * as yup from 'yup';
import { Check, Copy, Send, Share } from 'react-feather';
import { DocumentNode } from '@apollo/client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';

import { NavItem, Section, SectionTitle } from '../../Common/Layout';
import { RegistrationSummary } from '../helpers';
import {
  assetUrl,
  encodeUrl, filterPrefix, getServerErrors, mergeUrlParams,
} from '../../../common/helpers';
import { useCopyLink } from '../../../common/Common/CopyLink';
import { useNavigation } from '../../Common/useNavigation';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import { useParticipant } from '../ParticipantProvider';
import GetRegistrationSummaryForDashboardQuery from './GetRegistrationSummaryForDashboardQuery';
import InviteParticipantsMutation from './InviteParticipantsMutation';
import SellableLabel from '../../../common/Common/SellableLabel';
import UI from '../../../common/UI';
import config from '../../../config';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useScroll from '../../../common/useScroll';
import useShare from '../../../common/useShare';
import useValidation from '../../../common/useValidation';

const MAX_INVITATION_EMAILS = 250;

interface GroupedTicketCount {
  assignable_count: number;
  assigned_count: number;
  invited_count: number;
  total_count: number;
  ticket: { id: string; title: string; };
}

const groupTickets = (summary: { assigned_ticket_counts: RegistrationSummary['assigned_ticket_counts']; }) => {
  const groups: GroupedTicketCount[] = [];

  summary.assigned_ticket_counts.forEach((counts) => {
    const ticketId = counts.ticket.id;
    const index = groups.findIndex((group) => group.ticket.id === ticketId);

    if (index > -1) {
      groups[index].assignable_count += counts.assignable_count;
      groups[index].assigned_count += counts.assigned_count;
      groups[index].invited_count += counts.invited_count;
      groups[index].total_count += counts.total_count;
    } else {
      groups.push({
        assignable_count: counts.assignable_count,
        assigned_count: counts.assigned_count,
        invited_count: counts.invited_count,
        total_count: counts.total_count,
        ticket: counts.ticket,
      });
    }
  });

  return groups;
};

export interface UnassignedRegistrationsSectionProps {
  summary: RegistrationSummary;
  tab?: 'share_link' | 'send_invitations';
  refetchQueries?: DocumentNode[];
}

const UnassignedRegistrationsSection = ({
  summary, tab: initialTab = 'share_link', refetchQueries,
}: UnassignedRegistrationsSectionProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { participant } = useParticipant();
  const { formatNumber } = useLocale();
  const { scrollTo } = useScroll();
  const project = useProject();

  const groups = groupTickets(summary);

  const inputRef = useRef<HTMLInputElement>();
  const buttonRef = useRef<HTMLButtonElement>();

  useEffect(() => {
    const input = inputRef.current;

    if (input) {
      const { paddingRight } = input.style;
      input.style.paddingRight = `${Math.max((buttonRef.current?.offsetWidth || 0) + 10, 0)}px`;

      return () => {
        input.style.paddingRight = `${paddingRight}px`;
      };
    }

    return () => {};
  });

  const { canShare, handleShare } = useShare({
    text: `${t('claim_your_ticket_for_event_here', { event: summary.event.title })}: ${summary.claim_url}`,
  });

  const [copy, copied] = useCopyLink(summary.claim_url);

  const [tab, setTab] = useState(initialTab);

  const assignableTicketsCount = summary.assigned_ticket_counts.reduce(
    (count, group) => count + group.assignable_count,
    0,
  );

  const [initialAssignableTicketsCount] = useState(assignableTicketsCount);

  return (
    <>
      {participant && participant.is_charity && summary.event.enable_fundraising && (
        <UI.GridContainer sc={{ gutter: 0.75 }} id="UnassignedRegistrationsSection">
          <UI.Div sc={{ px: [2, 4] }}>
            <SectionTitle>
              {t('fundraising')}
            </SectionTitle>
          </UI.Div>
          <Section>
            <UI.Div>
              <UI.A
                href={config.supporta.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={assetUrl('images/logo-supporta-mono.svg')}
                  height={14}
                  alt="Supporta"
                />
              </UI.A>
              {!participant.charity && (
                <UI.Div sc={{ color: 'gray.500' }}>
                  <UI.Paragraph sc={{ mb: 3 }}>
                    {t('charity_fundraising_description')}
                  </UI.Paragraph>
                  <UI.LinkButton
                    to={`${r('Charity', { projectId: project.id, participantId: summary.participant.id })}?${encodeUrl({ event: summary.event.id, token: summary.view_token })}}`}
                    sc={{ brand: 'secondary', block: true }}
                  >
                    {t('configure_charity')}
                  </UI.LinkButton>
                </UI.Div>
              )}
              {participant.charity && (
                <UI.Div>
                  <Trans
                    i18nKey="fundraising_pages_will_be_created_for"
                    values={{
                      charity: participant.charity.title,
                    }}
                  >
                    <UI.Link
                      to={`${r('Charity', { projectId: project.id, participantId: summary.participant.id })}?${encodeUrl({ event: summary.event.id, token: summary.view_token })}}`}
                      sc={{ noWrap: true }}
                      style={{ fontWeight: 500 }}
                    />
                  </Trans>
                </UI.Div>
              )}
            </UI.Div>
          </Section>
        </UI.GridContainer>
      )}

      <UI.GridContainer sc={{ gutter: 0.75 }} id="UnassignedRegistrationsSection">
        <UI.Div sc={{ px: [2, 4] }}>
          <SectionTitle>
            {t('status')}
          </SectionTitle>
        </UI.Div>

        <Section>
          {groups.map((group) => (
            <NavItem
              to={mergeUrlParams({ registrations: { ticket: group.ticket.id } })}
              onClick={() => scrollTo('TicketsSection', { gutter: 1, duration: 200 })}
              title={group.ticket.title}
              key={group.ticket.id}
            >
              <UI.GridContainer sc={{ columns: '1fr fit-content(160px) 40px', alignVertical: 'center', gutter: 0.5 }}>
                <UI.Div sc={{ fontWeight: 500 }} style={{ minWidth: 0, display: 'flex' }}>
                  <SellableLabel style={{ minWidth: 0 }}>
                    <UI.Truncate>
                      <UI.Icon>
                        <UI.Icons.Ticket />
                      </UI.Icon>
                      {' '}
                      {group.ticket.title}
                    </UI.Truncate>
                  </SellableLabel>
                </UI.Div>
                <UI.Div sc={{ small: true, color: 'gray.500', noWrap: true, textAlign: 'right' }}>
                  {group.assignable_count > 0
                    ? t('n_remaining', { count: group.assignable_count, value: formatNumber(group.assignable_count) })
                    : (group.invited_count > 0 ? t('invited') : t('complete'))}
                </UI.Div>
                <ProgressBar
                  count={group.assigned_count}
                  secondary={group.invited_count}
                  capacity={group.total_count}
                />
              </UI.GridContainer>
            </NavItem>
          ))}

        </Section>
      </UI.GridContainer>

      {summary.event.active && initialAssignableTicketsCount > 0 && (
        <UI.GridContainer sc={{ gutter: 0.75 }} id="AssignTicketsSection">
          <UI.Div sc={{ px: [2, 4] }}>
            <SectionTitle>
              {t('assign_tickets')}
            </SectionTitle>
          </UI.Div>

          {assignableTicketsCount > 0 && (
            <Section spacing="md">
              <UI.Div sc={{ py: 0 }}>
                <UI.Div style={{ margin: '2px 0 -1px 0' }}>
                  <UI.TabButton
                    onClick={() => setTab('share_link')}
                    sc={{ active: tab === 'share_link', mr: 3 }}
                  >
                    {t('share_link')}
                  </UI.TabButton>
                  <UI.TabButton
                    onClick={() => setTab('send_invitations')}
                    sc={{ active: tab === 'send_invitations' }}
                  >
                    {t('send_invitations')}
                  </UI.TabButton>
                </UI.Div>
              </UI.Div>
              <UI.Div sc={{ background: 'gray.50' }}>
                {tab === 'share_link' && (
                  <UI.FadeIn>
                    <UI.FormGrid sc={{ gutter: 0.75 }}>
                      <UI.InputGroup>
                        <UI.InputLabel htmlFor="RegistrationLink">
                          {t('registration_link')}
                        </UI.InputLabel>
                        <UI.InputDescription>
                          {t('registration_link_description')}
                        </UI.InputDescription>
                        <UI.InputWrapper>
                          <UI.Input
                            defaultValue={summary.claim_url}
                            readOnly
                            onFocus={(event) => event.target.select()}
                            ref={inputRef}
                            sc={{ color: 'gray.500' }}
                            id="RegistrationLink"
                          />
                          <UI.Button
                            onClick={() => copy()}
                            sc={{ blank: true, noWrap: true }}
                            ref={buttonRef}
                            style={{ borderLeft: '1px solid rgba(0, 0, 0, .075)' }}
                          >
                            <UI.Icon>
                              {copied ? <Check /> : <Copy />}
                            </UI.Icon>
                            {' '}
                            {t('copy')}
                          </UI.Button>
                        </UI.InputWrapper>
                      </UI.InputGroup>

                      {canShare && (
                        <UI.Button onClick={handleShare} sc={{ brand: 'secondary' }}>
                          <UI.Icon>
                            <Share />
                          </UI.Icon>
                          {' '}
                          {t('share_link')}
                        </UI.Button>
                      )}
                    </UI.FormGrid>
                  </UI.FadeIn>
                )}
                {tab === 'send_invitations' && (
                  <UI.FadeIn>
                    <InvitationForm summary={summary} refetchQueries={refetchQueries} />
                  </UI.FadeIn>
                )}
              </UI.Div>
            </Section>
          )}

          {assignableTicketsCount === 0 && (
            <AllTicketsAssignedMessage />
          )}
        </UI.GridContainer>
      )}
    </>
  );
};

const inviteParticipantsSchema = (maxEmails: number) => yup.object({
  ticket_id: yup.string().nullable().required(),
  emails: yup.array(yup.string().optional()).max(maxEmails),
});

interface InvitationFormProps {
  summary: RegistrationSummary;
  refetchQueries?: DocumentNode[];
}

const InvitationForm = ({
  summary,
  refetchQueries = [GetRegistrationSummaryForDashboardQuery],
}: InvitationFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { formatNumber } = useLocale();
  const { scrollTo, scrollToIfInvisible } = useScroll();

  const groups = groupTickets(summary);

  const getValues = (groups: GroupedTicketCount[]) => {
    const availableTicketGroups = groups.filter(
      (group) => group.assignable_count > 0,
    );

    return {
      ticket_id: availableTicketGroups.length === 1 ? availableTicketGroups[0].ticket.id : '',
      emails: '',
      invite_duplicates: null as boolean | null,
    };
  };

  const form = useForm({
    values: getValues(groups),
  });

  const selectedTicketGroup = groups.find((group) => group.ticket.id === form.values.ticket_id);

  const totalCount = selectedTicketGroup ? selectedTicketGroup.assignable_count : 0;

  const maxCount = Math.min(totalCount, MAX_INVITATION_EMAILS);

  const [invitationsSentCount, setInvitationsSentCount] = useState(0);
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [duplicateEmails, setDuplicateEmails] = useState<string[]>([]);

  const emails = form.values.emails.split(/[\s\n,]+/).map(
    (value) => value.toLowerCase(),
  ).filter(
    (value) => value && (form.values.invite_duplicates || !duplicateEmails.includes(value)),
  );

  const [formKey, setFormKey] = useState(0);

  const summaryRef = useRef(summary);

  useEffect(() => {
    if (summaryRef.current !== summary) {
      form.reset(getValues(groupTickets(summary)));
      setFormKey((key) => key + 1);
      summaryRef.current = summary;
    }
  }, [form, summary]);

  const [inviteParticipants, { error, loading }] = useMutation(
    InviteParticipantsMutation,
    {
      variables: {
        input: {
          participant_id: summary.participant.id,
          ticket_id: form.values.ticket_id,
          emails,
          invite_duplicates: form.values.invite_duplicates,
        },
      },
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
      onCompleted: (data) => {
        setInvitationsSentCount(data.inviteParticipants.length);
        setInvalidEmails([]);
        setDuplicateEmails([]);

        notifier.success(t('invitations_sent', {
          count: data.inviteParticipants.length,
          value: formatNumber(data.inviteParticipants.length),
        }));
      },
      onError: (error, { variables }) => {
        const errors = getServerErrors(error);

        setInvalidEmails(
          Object.keys(filterPrefix(errors, 'emails', false)).map((index) => variables.input.emails[index]),
        );

        const duplicateEmails = errors?.invite_duplicates?.duplicate_emails.duplicates || [];
        setDuplicateEmails(duplicateEmails);

        if (duplicateEmails.length === 0) {
          scrollToIfInvisible('InviteParticipantsForm', { gutter: 1 });
        }
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: inviteParticipantsSchema(maxCount),
    values: {
      ticket_id: form.values.ticket_id,
      emails,
    },
    externalErrors: getServerErrors(error),
  });

  const duplicateEmailsRef = useRef(duplicateEmails);

  useEffect(() => {
    if (duplicateEmailsRef.current !== duplicateEmails) {
      form.set('invite_duplicates', duplicateEmails.length > 0 ? false : null);
      duplicateEmailsRef.current = duplicateEmails;
    }
  }, [form, duplicateEmails]);

  useEffect(() => {
    setDuplicateEmails([]);
  }, [form.values.emails, form.values.ticket_id]);

  const hasDuplicateEmailsError = !!(error?.graphQLErrors?.[0]?.extensions?.validation as any)
    ?.['input.invite_duplicates']?.duplicate_emails;

  return (
    <UI.Form onSubmit={() => inviteParticipants()} id="InviteParticipantsForm">
      <UI.FormGrid sc={{ gutter: 0.75 }}>
        {!hasDuplicateEmailsError && (
          invalidEmails.length > 0 ? (
            <UI.Error>
              <UI.Strong sc={{ block: true }}>
                {t('common:validation.something_wrong_with_input')}
              </UI.Strong>
              {invalidEmails.length > 0 && (
                <UI.ErrorMessagesContainer>
                  {t('invalid_email_addresses', { emails: invalidEmails.join(', '), count: invalidEmails.length })}
                </UI.ErrorMessagesContainer>
              )}
            </UI.Error>
          ) : (
            <UI.ServerErrors error={error} attributes={{ emails: t('email_addresses') }} />
          )
        )}

        <UI.InputGroup>
          <UI.InputLabel htmlFor="InvitationFormTicket">
            {t('ticket')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('invite_ticket_description')}
          </UI.InputDescription>
          <UI.InputWrapper>
            <UI.Select
              {...form.select('ticket_id', undefined, { onChange: () => setInvitationsSentCount(0) })}
              id="InvitationFormTicket"
            >
              {!form.values.ticket_id && <option value="" />}
              {groups.map((group) => (
                <option
                  value={group.ticket.id}
                  key={group.ticket.id}
                  disabled={group.assignable_count <= 0}
                >
                  {group.ticket.title}
                </option>
              ))}
            </UI.Select>
          </UI.InputWrapper>
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.emails, touched: form.touched.emails }}>
          <UI.InputLabel htmlFor="InvitationFormEmails">
            {t('email_addresses')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('invite_email_addresses_description')}
            {' '}
            <UI.Label
              sc={{
                small: true,
                muted: totalCount === 0,
                background: emails.length > totalCount ? 'error' : undefined,
                color: emails.length > totalCount ? 'white' : undefined,
              }}
            >
              <UI.Strong>
                {formatNumber(emails.length)}
              </UI.Strong>
              {' / '}
              {formatNumber(maxCount)}
            </UI.Label>
          </UI.InputDescription>
          <UI.TextArea
            {...form.textarea('emails', { onChange: () => setInvitationsSentCount(0) })}
            id="InvitationFormEmails"
            key={formKey}
          />
          <UI.ErrorMessages attribute={t('email_addresses')} errors={errors.emails} />
        </UI.InputGroup>

        {duplicateEmails.length > 0 && (
          <>
            <UI.Warning>
              <UI.H4>
                {t('confirmation_required')}
              </UI.H4>
              {t('duplicate_invited_emails_warning', {
                duplicates: duplicateEmails.join(', '),
              })}
            </UI.Warning>
            <UI.InputGroup>
              <UI.InputLabel>
                {t('duplicate_email_addresses')}
              </UI.InputLabel>
              <UI.Radio {...form.radio('invite_duplicates', false)}>
                {t('skip_duplicate_email_addresses')}
              </UI.Radio>
              <UI.Radio {...form.radio('invite_duplicates', true)}>
                {t('invite_duplicate_email_addresses_anyway')}
              </UI.Radio>
            </UI.InputGroup>
          </>
        )}

        <UI.Button
          type="submit"
          sc={{ brand: 'secondary', loading }}
          disabled={!valid || loading || emails.length === 0}
        >
          <UI.Icon>
            {loading ? <UI.Loader sc={{ brand: 'white' }} /> : <Send />}
          </UI.Icon>
          {' '}
          {t('send_invitations', { count: emails.length, value: formatNumber(emails.length) })}
        </UI.Button>

        {invitationsSentCount > 0 && (
          <UI.Div sc={{ textAlign: 'center' }}>
            <UI.Span sc={{ mx: 2, noWrap: true }}>
              <UI.Icon sc={{ color: 'success' }}>
                <Check />
              </UI.Icon>
              {' '}
              {t('invitations_sent', { count: invitationsSentCount, value: formatNumber(invitationsSentCount) })}
            </UI.Span>
            {' '}
            <UI.Link
              to={mergeUrlParams({
                registrations: { ticket: form.values.ticket_id, assignment_statuses: ['invited'] },
              })}
              onClick={() => scrollTo('TicketsSection', { gutter: 1, duration: 200 })}
              sc={{ fontWeight: 500, noWrap: true, mx: 2 }}
            >
              {t('view_all_invitations')}
            </UI.Link>
          </UI.Div>
        )}
      </UI.FormGrid>
    </UI.Form>
  );
};

interface ProgressBarProps {
  count: number;
  capacity: number;
  secondary: number;
  tooltip?: ReactNode;
}

export const ProgressBar = ({ count, capacity, secondary = 0, tooltip }: ProgressBarProps) => {
  const [progress, setProgress] = useState(0);
  const [extra, setExtra] = useState(0);

  const calculatedCapacity = Math.max(capacity, count + secondary);
  const calculatedProgress = Math.min(100, (count / calculatedCapacity) * 100);
  const calculatedExtra = Math.min(100, (secondary / calculatedCapacity) * 100);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setProgress(calculatedProgress);
      setExtra(calculatedExtra);
    }, 100);

    return () => window.clearTimeout(timer);
  }, [calculatedProgress, calculatedExtra]);

  return (
    <UI.TooltipContainer style={{ width: 40, height: 8 }}>
      {tooltip && (
        <UI.Tooltip sc={{ alignH: 'center', alignV: 'top', small: true }}>
          {tooltip}
        </UI.Tooltip>
      )}
      <UI.Div
        sc={{ background: 'gray.200' }}
        style={{
          height: '100%', width: '100%', borderRadius: '4px', overflow: 'hidden', display: 'flex',
        }}
      >
        <UI.Div
          sc={{ background: 'success' }}
          style={{
            height: '100%',
            width: `${progress}%`,
            transition: 'all 0.25s ease-in-out',
          }}
        />
        <UI.Div
          sc={{ background: 'info' }}
          style={{
            height: '100%',
            width: `${extra}%`,
            transition: 'all 0.25s ease-in-out',
          }}
        />
      </UI.Div>
    </UI.TooltipContainer>
  );
};

const AllTicketsAssignedMessage = () => {
  const { t } = useTranslation();
  const { scrollToIfInvisible } = useScroll();

  useEffect(() => {
    scrollToIfInvisible('AssignTicketsSection', { gutter: 1 });
  }, [scrollToIfInvisible]);

  return (
    <Section>
      <UI.FadeIn sc={{ background: 'gray.50' }}>
        <UI.FlexContainer sc={{ alignItems: 'center', spaceX: 2 }}>
          <UI.Badge sc={{ size: 24, brand: 'success' }}>
            <UI.Icon>
              <Check />
            </UI.Icon>
          </UI.Badge>
          <UI.Div>
            {t('all_tickets_assigned')}
          </UI.Div>
        </UI.FlexContainer>
      </UI.FadeIn>
    </Section>
  );
};

export default UnassignedRegistrationsSection;
